package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initScreenshotsWithoutAnEmulatorPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ScreenshotsWithoutAnEmulator.md", FrontMatterElement.Builder { addScalar("title", "Screenshot tests without an emulator"); addScalar("searchTitle", "Compose Multiplatform screenshot testing without an emulator"); addScalar("date", "2025-10-19"); addScalar("description", "viddik renders Compose Multiplatform through a real Compose Desktop window instead of LayoutLib, so goldens stay the same on macOS, Linux and Windows."); addScalar("library", "viddik"); }))
}

@Page("/blog/screenshots-without-an-emulator")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ScreenshotsWithoutAnEmulatorPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Screenshot testing on Android has a good answer — Paparazzi renders through LayoutLib, no emulator, fast. Compose Multiplatform does not, because LayoutLib is Android's renderer and the code under test is not Android code.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
            org.jetbrains.compose.web.dom.Text("viddik")
        }
        org.jetbrains.compose.web.dom.Text(" takes the other route: render through a real Compose Desktop window on Skiko, on a plain JVM. No emulator, no AVD, and — this is the part that matters — the same renderer that will actually draw the component.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-annotation-two-outputs") }) {
        org.jetbrains.compose.web.dom.Text("One annotation, two outputs")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@ViddikScreenshot
@Composable
fun EmptyBasket() = BasketScreen(state = Basket.empty)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A KSP processor collects every annotated composable into a registry, and that registry is consumed twice.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ViddikEngine")
        }
        org.jetbrains.compose.web.dom.Text(" captures each one to PNG and diffs it against the golden, in record or verify mode, as a JUnit 5 test. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ViddikShowroom")
        }
        org.jetbrains.compose.web.dom.Text(" takes the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("same")
        }
        org.jetbrains.compose.web.dom.Text(" registry and renders it as a live, browsable component gallery — the Showkase half of the idea, without a second set of declarations to keep in sync.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is the reason the two features live in one library. A component browser that drifts from the tests is worse than no browser at all.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-artefacts-and-why") }) {
        org.jetbrains.compose.web.dom.Text("Three artefacts, and why")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The module split is not organisational tidiness; it is about what may appear on which classpath.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("module")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("what it is")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("where it may go")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("viddik-annotations")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
                    }
                    org.jetbrains.compose.web.dom.Text(" marker, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ViddikComponent")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ViddikShowroom")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("anywhere, including ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("android()")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("viddik-processor")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("KSP codegen — registry and generated tests")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("a KSP configuration")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("viddik-testing-core")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("capture, diff and record, on Compose Desktop + JUnit 5")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("test")
                    }
                    org.jetbrains.compose.web.dom.Text(" only, never ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("main")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The annotations artefact is deliberately tiny because it is the one that has to be safe to depend on from production source sets. The capture engine drags in Compose Desktop and JUnit, and has no business anywhere near an app's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-one-that-catches-everyone") }) {
        org.jetbrains.compose.web.dom.Text("The one that catches everyone")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Wiring KSP for a KMP target is where the time goes, and the failure is quiet — the processor simply does not run and your registry is empty.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""dependencies {
    testImplementation("ru.workinprogress:viddik-annotations:<VERSION>")
    testImplementation("ru.workinprogress:viddik-testing-core:<VERSION>")
    add("kspDesktopTest", "ru.workinprogress:viddik-processor:<VERSION>")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kspDesktopTest")
        }
        org.jetbrains.compose.web.dom.Text(" configuration name is derived from your target name. Name your JVM target something else and that string changes with it; use a plain ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kotlin(\"jvm\")")
        }
        org.jetbrains.compose.web.dom.Text(" project and it is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kspTest")
        }
        org.jetbrains.compose.web.dom.Text(", with per-target artifact coordinates instead of the base ones. There is no error for getting this wrong, only zero generated tests — which is exactly the shape of failure worth knowing about in advance.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("goldens-that-travel") }) {
        org.jetbrains.compose.web.dom.Text("Goldens that travel")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reason to care about the renderer is that goldens get committed, and then somebody else runs the suite.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Because the capture goes through Compose Desktop rather than a platform toolkit, the same golden holds on macOS, Linux and Windows. That is what makes goldens reviewable in a pull request instead of a source of noise every time CI changes machines.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/viddik")
        }
    }
}
